import { Button } from "@dash/form";
import { useToast } from "@dash/toast";
import Decimal from "decimal.js";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";

import { Currency, Invoice, ThreadDto } from "@megaron/invoices-contracts";
import { useClientManager } from "@megaron/react-clients";

import { ThreadDetailsForm } from "../draft/ThreadDetailsForm";

type Props = {
  thread: ThreadDto;
  onClose: () => void;
  queryKey: string | string[];
};

export const EditInvoiceBody: React.FC<Props> = ({ thread, onClose, queryKey }) => {
  const queryClient = useQueryClient();
  const updateInvoiceMutation = useClientManager("invoices").updateInvoice().useMutation();
  const setThreadPublicMutation = useClientManager("invoices").setThreadPublic().useMutation();

  const toast = useToast();

  const invoice = thread.invoice;
  const threadUuid = thread.uuid;

  const hasAnySignature = thread.budgetSignatures.length > 0 || thread.validitySignatures.length > 0;
  const isPublicDisabled = thread.isPublic || hasAnySignature;

  const [invoiceNumber, setInvoiceNumber] = useState(invoice.invoiceNumber);
  const [issuer, setIssuer] = useState(invoice.issuer);
  const [issuerTaxId, setIssuerTaxId] = useState(invoice.issuerTaxId);
  const [total, setTotal] = useState(invoice.total.toFixed(2));
  const [currency, setCurrency] = useState<Currency>(invoice.currency);
  const [issueDate, setIssueDate] = useState(invoice.issueDate);
  const [dueDate, setDueDate] = useState(invoice.dueDate);
  const [receivedDate, setReceivedDate] = useState(invoice.receivedDate);
  const [isDigital, setIsDigital] = useState(invoice.isDigital);
  const [isPayed, setIsPayed] = useState(!invoice.dueDate);
  const [isPublic, setIsPublic] = useState(thread.isPublic);

  useEffect(() => {
    if (dueDate && isPayed) {
      setDueDate(null);
    }

    if (!dueDate && !isPayed) {
      setDueDate(new Date());
    }
  }, [dueDate, isPayed, setDueDate]);

  useEffect(() => {
    setInvoiceNumber(invoice.invoiceNumber);
    setTotal(invoice.total.toFixed(2));
    setCurrency(invoice.currency);
    setIssueDate(invoice.issueDate);
    setDueDate(invoice.dueDate);
    setReceivedDate(invoice.receivedDate);
    setIsDigital(invoice.isDigital);
    setIssuer(invoice.issuer);
    setIssuerTaxId(invoice.issuerTaxId);
    setIsPayed(!invoice.dueDate);
    setIsPublic(thread.isPublic);
  }, []);

  const updateInvoice = async () => {
    if (!invoiceNumber || !issueDate || !issuer || !receivedDate || !total || (!isPayed && !dueDate)) {
      toast.error("Uzupełnij brakujące pola w formularzu");
      return;
    }

    const updatedInvoice: Invoice = {
      ...invoice,
      invoiceNumber,
      issuer,
      total: new Decimal(Number(total || 0)),
      currency,
      issueDate,
      dueDate: dueDate ? new Date(dueDate) : null,
      receivedDate,
      isDigital,
      issuerTaxId,
    };

    try {
      await updateInvoiceMutation.mutateAsync({
        invoice: updatedInvoice,
        threadUuid,
      });
    } catch {
      toast.error("Nie udało się zaktualizować faktury");
      return;
    }

    if (isPublic && !thread.isPublic) {
      try {
        await setThreadPublicMutation.mutateAsync({ threadUuid });
      } catch {
        queryClient.invalidateQueries(queryKey);
        toast.error("Nie udało się ustawić faktury jako publicznej");
        return;
      }
    }

    queryClient.invalidateQueries(queryKey);
    onClose();
  };

  return (
    <div css={{ display: "flex", flexDirection: "column" }}>
      <ThreadDetailsForm
        values={{
          currency,
          dueDate,
          invoiceNumber,
          isDigital,
          isPublic,
          isPayed,
          issueDate,
          issuer,
          issuerTaxId,
          receivedDate,
          total,
        }}
        handlers={{
          onCurrencyChange: setCurrency,
          onDueDateChange: setDueDate,
          onInvoiceNumberChange: setInvoiceNumber,
          onIsDigitalChange: setIsDigital,
          onPayedChange: setIsPayed,
          onIssueDateChange: setIssueDate,
          onIssuerChange: setIssuer,
          onIssuerTaxIdChange: setIssuerTaxId,
          onIsPublicChange: setIsPublic,
          onReceivedDateChange: setReceivedDate,
          onTotalChange: setTotal,
        }}
        isPublicDisabled={isPublicDisabled}
      />
      <Button
        css={{ alignSelf: "flex-end", margin: "0.25rem 0" }}
        onClick={updateInvoice}
        isLoading={updateInvoiceMutation.isLoading || setThreadPublicMutation.isLoading}
      >
        Zapisz
      </Button>
    </div>
  );
};
